/**
 * Parse task mentions (#TASK-123 or #123) in chat messages
 * @param {string} text - Message text
 * @returns {Array} - Array of {text, isTask, taskId}
 */
export const parseTaskMentions = (text) => {
  if (!text) return [{text: '', isTask: false}];
  
  // Regex to match #TASK-xxx or #xxx (where xxx is numbers)
  const taskRegex = /#(?:TASK-)?(\w+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = taskRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({
        text: text.substring(lastIndex, match.index),
        isTask: false
      });
    }

    // Add the task mention
    parts.push({
      text: match[0],
      isTask: true,
      taskId: match[1]
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      text: text.substring(lastIndex),
      isTask: false
    });
  }

  return parts.length > 0 ? parts : [{text, isTask: false}];
};

/**
 * Parse slash commands in chat input
 * @param {string} text - Input text
 * @returns {Object} - {isCommand, command, args, taskId, value, error}
 */
export const parseSlashCommand = (text) => {
  if (!text || !text.startsWith('/')) {
    return {isCommand: false};
  }

  const parts = text.trim().split(/\s+/);
  const command = parts[0].substring(1).toLowerCase(); // Remove leading /
  const args = parts.slice(1);

  const result = {
    isCommand: true,
    command,
    args,
    rawText: text
  };

  // Parse specific commands
  switch(command) {
    case 'create-task':
    case 'task':
      // /create-task Title of the task
      result.title = args.join(' ');
      if (!result.title) result.error = 'Task title is required';
      break;

    case 'assign':
      // /assign @user #TASK-123 or /assign #TASK-123 @user
      result.taskId = extractTaskId(text);
      // Extract @mentions
      const mentionMatch = text.match(/@(\w+)/);
      result.assignee = mentionMatch ? mentionMatch[1] : null;
      if (!result.taskId) result.error = 'Task ID is required (e.g., #TASK-123)';
      if (!result.assignee) result.error = 'User mention is required (e.g., @john)';
      break;

    case 'status':
      // /status #TASK-123 done
      result.taskId = extractTaskId(text);
      // Get status (last arg if no task id, or arg after task id)
      const statusArg = args.find(arg => !arg.startsWith('#'));
      result.status = statusArg?.toLowerCase();
      if (!result.taskId) result.error = 'Task ID is required';
      if (!result.status) result.error = 'Status is required (todo/in-progress/done)';
      break;

    case 'done':
    case 'complete':
      // /done #TASK-123 (shortcut for /status #TASK-123 done)
      result.taskId = extractTaskId(text);
      result.status = 'done';
      if (!result.taskId) result.error = 'Task ID is required';
      break;

    case 'due':
    case 'deadline':
      // /due #TASK-123 2024-12-31 or /due #TASK-123 tomorrow
      result.taskId = extractTaskId(text);
      result.date = args.find(arg => !arg.startsWith('#'));
      if (!result.taskId) result.error = 'Task ID is required';
      if (!result.date) result.error = 'Date is required';
      break;

    case 'priority':
      // /priority #TASK-123 high
      result.taskId = extractTaskId(text);
      const priorityArg = args.find(arg => !arg.startsWith('#'));
      result.priority = priorityArg?.toLowerCase();
      if (!result.taskId) result.error = 'Task ID is required';
      if (!result.priority) result.error = 'Priority is required (urgent/high/medium/low)';
      break;

    case 'comment':
      // /comment #TASK-123 This is a comment
      result.taskId = extractTaskId(text);
      const taskIndex = args.findIndex(arg => arg.startsWith('#'));
      result.comment = taskIndex >= 0 ? args.slice(taskIndex + 1).join(' ') : args.join(' ');
      if (!result.taskId) result.error = 'Task ID is required';
      if (!result.comment) result.error = 'Comment text is required';
      break;

    case 'help':
      result.showHelp = true;
      break;

    default:
      result.error = `Unknown command: /${command}. Type /help for available commands.`;
  }

  return result;
};

/**
 * Extract task ID from text (#TASK-123 or #123)
 * @param {string} text
 * @returns {string|null}
 */
export const extractTaskId = (text) => {
  const match = text.match(/#(?:TASK-)?(\w+)/);
  return match ? match[1] : null;
};

/**
 * Get all available slash commands with descriptions
 * @returns {Array} - Array of command objects
 */
export const getAvailableCommands = () => {
  return [
    { command: '/create-task', args: '[title]', description: 'Create a new task quickly' },
    { command: '/assign', args: '@user #TASK-123', description: 'Assign a task to a user' },
    { command: '/status', args: '#TASK-123 [status]', description: 'Change task status (todo/in-progress/done)' },
    { command: '/done', args: '#TASK-123', description: 'Mark task as completed' },
    { command: '/due', args: '#TASK-123 [date]', description: 'Set task deadline' },
    { command: '/priority', args: '#TASK-123 [priority]', description: 'Change task priority (urgent/high/medium/low)' },
    { command: '/comment', args: '#TASK-123 [text]', description: 'Add a comment to a task' },
    { command: '/help', args: '', description: 'Show all available commands' }
  ];
};

/**
 * Parse date string to ISO format
 * @param {string} dateStr - Date string (today, tomorrow, YYYY-MM-DD)
 * @returns {string} - ISO date string
 */
export const parseDateString = (dateStr) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch(dateStr?.toLowerCase()) {
    case 'today':
      return today.toISOString().split('T')[0];
    case 'tomorrow':
      today.setDate(today.getDate() + 1);
      return today.toISOString().split('T')[0];
    case 'next week':
      today.setDate(today.getDate() + 7);
      return today.toISOString().split('T')[0];
    default:
      // Assume YYYY-MM-DD format
      return dateStr;
  }
};
